import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import { css, ThemeProvider } from "@emotion/react";
import { DataGridOverlay } from "./DataGridOverlay.tsx";

import { type GridSlotProps, useGridRootProps } from "@mui/x-data-grid-premium";
import NoRowsOverlayImage from "src/assets/illustrations/empty_box.svg?react";
import { H4 } from "src/components/Heading/Heading.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { DataGridNoResultsOverlay } from "src/components/DataGrid/DataGridNoResultsOverlay.tsx";
import { observer } from "mobx-react-lite";
import { useDataGridStore } from "src/components/DataGrid/DataGridStoreProvider.tsx";

export const DataGridNoRowsOverlay = observer(
  (props: GridSlotProps["noRowsOverlay"]) => {
    const theme = useNonConflictingTheme();
    const { t } = useTranslation(TranslationNamespaceEnum.DataGridComponent);
    const dataGridStore = useDataGridStore();

    // const api = useGridApiContext();
    // const filterModel = useGridSelector(api, gridFilterModelSelector);
    const rootProps = useGridRootProps();
    const { title = t("noRows"), Image: ImageProp } = props;

    const Image = ImageProp || NoRowsOverlayImage;

    // Solange geladen wird, darf keine Leermeldung erscheinen: sie ist eine Aussage über
    // ein Ergebnis, das noch gar nicht vorliegt. Auf grossen Listen stand bisher
    // sekundenlang „nichts vorhanden“, bevor tausende Zeilen erschienen – wer in dieser
    // Zeit hinsah, ging wieder.
    if (rootProps.loading) {
      return null;
    }

    const hasServerSideFilters =
      dataGridStore.appliedFilterModel.items.some((item) => !!item.value) &&
      rootProps.filterMode === "server";

    if (hasServerSideFilters) {
      return <DataGridNoResultsOverlay />;
    }

    return (
      <ThemeProvider theme={theme}>
        <DataGridOverlay>
          {/* todo: maybe text should be under image? */}
          <H4 css={styles.title}>{title}</H4>
          <Image css={styles.image} />
        </DataGridOverlay>
      </ThemeProvider>
    );
  },
);

const styles = {
  image: (theme: ExactTheme) =>
    css({
      color: theme.colorsV2.grey[600],
      "@media (max-height: 400px)": {
        display: "none",
      },
    }),
  title: css({
    textAlign: "center",
  }),
};
